import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  useForm,
  useFieldArray,
  type Control,
  type FieldErrors,
  type UseFormHandleSubmit,
  type UseFormRegister,
  type UseFieldArrayAppend,
  type UseFieldArrayPrepend,
  type UseFieldArrayRemove,
  type UseFieldArrayUpdate,
  type FieldArrayWithId,
  UseFieldArrayReplace,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { setIsAddLead, setIsEditMode } from "../stores/leadFunctionalSlice";
import { useLeadMutations } from "../api/useLeadMutations";
import { leadDefaultValues, LeadSchema, NoteSchema } from "../types/zodTypes";
import useCurrentLead from "../api/useCurrentLead";
import { useLeadInvisibility } from "../api/useLeadInvisibility";
import { useSyncronizer } from "@/hooks/useSyncronizer";
import { formatDate } from "@/utils/date";
import { ChildFormProvider } from "./ChildProvider";
import IndustryContextWrapper from "./IndustryContextWrapper";
import {
  DEFAULT_COUNTRY_CODE,
  splitStoredPhone,
} from "@/constants/countryCodes";
import {
  joinWhatsapp,
  splitStoredWhatsapp,
} from "@/features/leads/utilities/formatLeadWhatsapp";
import { normalizeCustomInputScopesForForm } from "@/utils/getFromCustom";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { lead_spesific_key } from "@/config/endpoints";
import {
  useLeadFormRequiredFields,
  useAllowCreatedAtEdit,
  useAddLeadHiddenFields,
} from "../api/useLeadFormConfig";
import {
  effectiveRequiredKeys,
  missingRequiredFields,
} from "../utils/requiredFields";

type LeadFormData = z.infer<typeof LeadSchema>;
type NoteData = z.infer<typeof NoteSchema>;

export interface FormLeadContextType {
  register: UseFormRegister<LeadFormData>;
  control: Control<LeadFormData>;
  errors: FieldErrors<LeadFormData>;
  handleSubmit: UseFormHandleSubmit<LeadFormData>;
  watch: any;
  getValues: any;
  setValue: any;
  reset: () => void;
  onSubmit: (data: LeadFormData) => void;
  isSubmitting: boolean;
  defaultValues: LeadFormData;
  lead: any;
  refetchCurLead: any;

  noteOperations: {
    fields: FieldArrayWithId<LeadFormData, "notes", "id">[];
    append: UseFieldArrayAppend<LeadFormData, "notes">;
    prepend: UseFieldArrayPrepend<LeadFormData, "notes">;
    remove: UseFieldArrayRemove;
    update: UseFieldArrayUpdate<LeadFormData, "notes">;
  };

  followUpOperations: {
    fields: FieldArrayWithId<LeadFormData, "followUps", "id">[];
    append: UseFieldArrayAppend<LeadFormData, "followUps">;
    prepend: UseFieldArrayPrepend<LeadFormData, "followUps">;
    remove: UseFieldArrayRemove;
    update: UseFieldArrayUpdate<LeadFormData, "followUps">;
    replace: UseFieldArrayReplace<LeadFormData, "followUps">;
  };

  deadlineOperations: {
    fields: FieldArrayWithId<LeadFormData, "deadlines", "id">[];
    append: UseFieldArrayAppend<LeadFormData, "deadlines">;
    prepend: UseFieldArrayPrepend<LeadFormData, "deadlines">;
    remove: UseFieldArrayRemove;
    update: UseFieldArrayUpdate<LeadFormData, "deadlines">;
    replace: UseFieldArrayReplace<LeadFormData, "deadlines">;
  };

  /** Read-only: revoked list filter active, or API set `isAccessRevoked` on the lead */
  isViewLocked: boolean;
  revokedBranch: boolean;
  revokedUser: boolean;
}

export const FormLeadContext = createContext<FormLeadContextType | undefined>(
  undefined
);

interface LeadFormProviderProps {
  children: ReactNode;
  onSuccess?: () => void;
  leadId?: string | number | undefined;
  /** Forwarded to ChildFormProvider so the deal panel default-selects the
   *  child in the current stage route. Undefined outside the dynamic routes. */
  routeStageId?: number | null;
  /** With `routeStageId`: the list's status / sub-status chips, so the deal
   *  default-selected is the one carrying every chip (see ViewLead). */
  routeStatusId?: number | null;
  routeSubStatusId?: number | null;
  /** Forwarded to ChildFormProvider: default-select THIS deal on open (wins
   *  over routeStageId). Set by the dashboard follow-up rail — the deal whose
   *  slot put the lead in the viewer's bucket. */
  defaultChildId?: number | null;
}

export const onInvalid = (errors: FieldErrors<LeadFormData>) => {
  const errorKeys = Object.keys(errors) as Array<keyof LeadFormData>;
  if (errorKeys.length > 0) {
    const firstError = errors[errorKeys[0]]?.message;
    toast.error(firstError || "Please check the form for errors");
  }
};

function LeadFormProvider({
  children,
  onSuccess,
  leadId,
  routeStageId,
  routeStatusId,
  routeSubStatusId,
  defaultChildId,
}: LeadFormProviderProps) {
  const dispatch = useAppDispatch();
  const { createLead, updateLead } = useLeadMutations();
  const { isEditMode } = useAppSelector((state) => state.leadFunctional);
  const revokedFilterOn = useAppSelector(
    (state) =>
      state.leadFilterSlice.appliedFilters?.isRevoked === true ||
      state.leadFilterSlice.appliedFilters?.isRevoked === "true" ||
      state.leadFilterSlice.appliedFilters?.isRevoked === 1
  );
  const { lead, refetch: refetchCurLead } = useCurrentLead(leadId);

  // List-level signals (`isAccessRevoked` server flag, or the Revoked filter
  // being on) don't know whether THIS specific lead is actually inaccessible.
  // Confirm with the per-lead invisibility diagnostic before locking the view —
  // mirrors the guard `LeadInvisibilityBanner` already uses. If the diagnostic
  // says `hasAccess === true` (e.g. CF in LOW mode where read is unrestricted,
  // or a previously-revoked assignment replaced by an active one), the lead is
  // fully interactive even when surfaced from the Revoked tab.
  const lockTriggered = Boolean(
    (lead as any)?.isAccessRevoked === true || revokedFilterOn
  );
  const numericLeadId =
    leadId == null || leadId === "" ? null : Number(leadId) || null;
  const { data: invisibilityData, isLoading: invisibilityLoading } =
    useLeadInvisibility(numericLeadId, {
      enabled: lockTriggered && !!numericLeadId,
    });
  // While the diagnostic is in flight, stay locked (safer default). Unlock
  // once data confirms access. If the call errored / returned nothing, fall
  // back to the trigger so today's behaviour is preserved.
  const isViewLocked = lockTriggered
    ? invisibilityLoading
      ? true
      : invisibilityData
        ? !invisibilityData.hasAccess
        : true
    : false;

  useEffect(() => {
    if (isViewLocked) {
      dispatch(setIsEditMode(false));
    }
  }, [isViewLocked, dispatch]);

  useEffect(() => {
    if (leadId) {
      refetchCurLead();
    } else {
      reset(leadDefaultValues);
    }
  }, [leadId]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<LeadFormData>({
    resolver: zodResolver(LeadSchema),
    defaultValues: leadDefaultValues,
    mode: "onChange",
  });

  const {
    fields: noteFields,
    append: noteAppend,
    prepend: notePrepend,
    remove: noteRemove,
    update: noteUpdate,
  } = useFieldArray({
    control,
    name: "notes",
  });

  const {
    fields: followUpFields,
    append: followUpAppend,
    prepend: followUpPrepend,
    remove: followUpRemove,
    update: followUpUpdate,
    replace: followUpReplace,
  } = useFieldArray({
    control,
    name: "followUps",
  });

  const {
    fields: deadlineFields,
    append: deadlineAppend,
    prepend: deadlinePrepend,
    remove: deadlineRemove,
    update: deadlineUpdate,
    replace: deadlineReplace,
  } = useFieldArray({
    control,
    name: "deadlines",
  });

  /**
   * Bumps when documents OR core lead fields from the API change so RHF resets.
   * (Previously only documents were keyed — phone/location never re-synced after save.)
   * Includes follow-ups so the footer "Follow-up" date (`followUpDate`) updates when
   * the list is rescheduled in the Follow-ups tab (deadlines were already keyed).
   * `customSig` keeps the lead-level Preference panel in sync when custom scopes
   * are mutated server-side outside this form (mirrors ChildProvider's fix).
   */
  const leadFormSyncKey = useMemo(() => {
    if (!lead?.id) return null;
    const docs = (lead as any).documents ?? [];
    const docSig = docs
      .map(
        (d: any) =>
          `${d.id}:${d.folderId ?? ""}:${d.attachmentLabel ?? ""}:${String(d.name ?? "")}:${String(d.fileName ?? "")}`
      )
      .sort()
      .join("|");
    const coreSig = [
      (lead as any).phone,
      (lead as any).phoneCountryCode,
      (lead as any).location,
      (lead as any).name,
      (lead as any).email,
      (lead as any).whatsapp,
      (lead as any).gender,
      String((lead as any).branchId ?? ""),
      String((lead as any).leadSource?.id ?? ""),
      String((lead as any).campaign?.id ?? ""),
      (lead as any).updatedAt,
    ].join("|");
    const followUpSig = ((lead as any).followUpDates ?? [])
      .map(
        (f: any) =>
          `${f.id}:${f.status}:${f.date ?? ""}:${f.updatedAt ?? ""}`
      )
      .join("|");
    const deadlineSig = ((lead as any).deadlineDates ?? [])
      .map(
        (d: any) =>
          `${d.id}:${d.status}:${d.updatedAt ?? ""}:${d.date ?? ""}:${d.fromStatusId ?? ""}:${d.toStatusId ?? ""}:${d.fromSubStatusId ?? ""}:${d.toSubStatusId ?? ""}:${d.warningDays ?? ""}:${d.triggeredAt ?? ""}`
      )
      .join("|");
    const customSig = ((lead as any).customInputScopes ?? [])
      .map(
        (s: any) =>
          `${s?.customInputId ?? ""}:${((s?.optionIds ?? []) as any[])
            .map((o) => String(o))
            .sort()
            .join(",")}:${s?.textValue ?? ""}`
      )
      .sort()
      .join("|");
    // Notes the server added / edited / deleted since the last seed. Without
    // it a refetch that only brought notes left the panel as it was — and
    // with "Chat & notes sync" every chat message becomes a note the user
    // expects to see in Notes after sending it.
    const notesSig = ((lead as any).notes ?? [])
      .map(
        (n: any) => `${n.id}:${n.isDeleted ? 1 : 0}:${n.editedAt ?? ""}`
      )
      .join("|");
    return `${(lead as any).id}:${docs.length}:${docSig}:${coreSig}:${followUpSig}:${deadlineSig}:${customSig}:${notesSig}`;
  }, [lead]);

  useSyncronizer(leadFormSyncKey, () => {
    if (lead?.id && leadId) {
      const splitPhone = splitStoredPhone(
        (lead as any).phone,
        (lead as any).phoneCountryCode,
      );
      // WhatsApp is ONE stored string; the form edits it as code + number
      // (formatLeadWhatsapp.ts) and `onSubmit` joins them back. Its code is
      // its own — independent of the phone's (owner, 2026-10-25).
      const splitWhatsapp = splitStoredWhatsapp(lead?.whatsapp);
      reset({
        notes: lead.notes.map((val: any) => ({
          note: val.content,
          createdById: String(val.createdBy.id),
          createdByName: val.createdBy.name,
          // "Switch user" byline — see authorByline.ts. Null on ordinary notes.
          switchedByName: val.switchedByUser?.name ?? null,
          createdAt: formatDate(val.createdAt),
          noteId: val.id,
          isDeleted: Boolean(val.isDeleted),
          editedAt: val.editedAt ?? null,
        })),
        followUps: lead?.followUpDates.map((val: any) => {
          return {
            id: val.id,
            createdBy: val.createdBy,
            date: val.date,
            isSystem: val.isSystem,
            createdAt: val.createdAt,
            status: val.status,
            // THE SLOT (role-based follow-ups). This mapper hand-picks
            // fields, and dropping these here starved every role UI
            // downstream — keep them forever.
            roleId: val.roleId ?? null,
            role: val.role ?? null,
            note: val?.note
              ? {
                  note: val.note?.content,
                  createdAt: val.note.createdAt,
                  createdByName: val.note.createdBy.name,
                }
              : {},
            completedBy: val.completedBy,
          };
        }),
        deadlines: (lead?.deadlineDates ?? []).map((val: any) => ({
          id: val.id,
          createdBy: val.createdBy,
          date: val.date,
          isSystem: val.isSystem,
          createdAt: val.createdAt,
          status: val.status,
          fromStatusId: val.fromStatusId,
          toStatusId: val.toStatusId,
          fromSubStatusId: val.fromSubStatusId,
          toSubStatusId: val.toSubStatusId,
          fromStatus: val.fromStatus
            ? { ...val.fromStatus, stageId: val.fromStatus.stageId }
            : val.fromStatus,
          toStatus: val.toStatus
            ? { ...val.toStatus, stageId: val.toStatus.stageId }
            : val.toStatus,
          fromSubStatus: val.fromSubStatus,
          toSubStatus: val.toSubStatus,
          note: val?.note
            ? {
                note: val.note?.content,
                createdAt: val.note.createdAt,
                createdByName: val.note.createdBy.name,
              }
            : {},
          completedBy: val.completedBy,
          warningDays: val.warningDays ?? null,
          triggeredAt: val.triggeredAt ?? null,
          updatedAt: val.updatedAt ?? null,
        })),
        followUpDate: lead?.followUpDates.find(
          (val: any) => val.status === "Pending"
        )?.date,
        name: lead?.name ?? "Add Name",
        phoneCountryCode: splitPhone.countryCode || DEFAULT_COUNTRY_CODE,
        phone: splitPhone.national,
        branchId: String(lead.branchId) ?? "",
        whatsapp: splitWhatsapp?.national ?? "",
        whatsappCountryCode: splitWhatsapp?.countryCode || DEFAULT_COUNTRY_CODE,
        email: lead?.email ?? "",
        gender: lead?.gender ?? "Male",
        location: lead?.location ?? "",
        customInputScopes: normalizeCustomInputScopesForForm(
          lead.customInputScopes,
        ),
        documents: lead?.documents ?? [],
        leadSourceId: lead?.leadSource?.id ? String(lead.leadSource.id) : "",
        campaignId: lead?.campaign?.id ? String(lead.campaign.id) : "",
        // Full ISO so an untouched Created At round-trips byte-identically;
        // `onSubmit` drops it from the PATCH unless it actually changed.
        createdAt: (lead as any)?.createdAt ?? undefined,
      });
    }
  });

  // Org-wide "Add/Edit Created At" provision (see the Manage section). The
  // field is rendered elsewhere only when this is on; here it decides whether
  // a `createdAt` may leave the form at all — the server refuses it while off.
  const allowCreatedAtEdit = useAllowCreatedAtEdit();

  // Tenant-wide required-fields policy (Super-Admin-set, enforced for everyone).
  // The server is authoritative; this pre-check gives immediate feedback + names
  // the missing fields before a round-trip. Custom-field labels resolved here.
  const { requiredFields } = useLeadFormRequiredFields();
  const hiddenFields = useAddLeadHiddenFields();
  const { spesificFields } = useSpesificCustoms({
    queryKey: lead_spesific_key,
    modelName: "leads",
  });
  const prefLabelFor = useCallback(
    (id: string) => {
      const f = (spesificFields ?? []).find((x: any) => String(x.id) === id);
      return getCfDisplayName(f) || "a required field";
    },
    [spesificFields],
  );

  const onSubmit = useCallback(
    (data: LeadFormData) => {
      if (isViewLocked) {
        toast.error("This lead is revoked. You cannot edit it.");
        return;
      }
      // Required-fields gate — only when CREATING a lead (the policy is about the
      // Add Lead form). Edits keep their existing behaviour.
      if (!isEditMode) {
        const missing = missingRequiredFields(
          data,
          effectiveRequiredKeys(requiredFields, hiddenFields),
          prefLabelFor,
          // Until the field catalog has loaded, leave it to the server.
          spesificFields
            ? new Set((spesificFields as any[]).map((f) => String(f.id)))
            : undefined,
        );
        if (missing.length > 0) {
          toast.error(
            `Please fill the required field${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}`,
          );
          return;
        }
      }
      // Created At leaves the form only when the org allows it AND (on edit)
      // it actually changed — re-sending the stored value would be a no-op
      // write the server still has to validate, and a stale client with the
      // provision turned off mid-session would otherwise be 403'd on every
      // ordinary save.
      if (
        !allowCreatedAtEdit ||
        (isEditMode && data.createdAt === (lead as any)?.createdAt)
      ) {
        data = { ...data };
        delete data.createdAt;
      }
      // The server keeps WhatsApp as ONE string ("+<cc> <national>"); the
      // picker's code is a form-only field and must not reach the request.
      // An UNTOUCHED number goes back exactly as stored (a legacy "98…" row
      // must not come back as "+91 98…" just because the form was saved):
      // only a changed code or number is joined.
      {
        const { whatsappCountryCode, ...rest } = data;
        const stored = isEditMode ? (lead?.whatsapp ?? "") : "";
        // Neither side reads the phone's code: changing the phone's country
        // never changes the WhatsApp number's.
        const storedSplit = isEditMode ? splitStoredWhatsapp(stored) : null;
        const untouched =
          isEditMode &&
          (rest.whatsapp ?? "") === (storedSplit?.national ?? "") &&
          (whatsappCountryCode || DEFAULT_COUNTRY_CODE) ===
            (storedSplit?.countryCode || DEFAULT_COUNTRY_CODE);
        data = {
          ...rest,
          whatsapp: untouched
            ? String(stored)
            : joinWhatsapp(whatsappCountryCode || DEFAULT_COUNTRY_CODE, rest.whatsapp ?? ""),
        };
      }
      if (isEditMode && leadId) {
        updateLead.mutate(
          { id: leadId, data },
          {
            onSuccess: () => {
              // Toast + refetch live in useLeadMutations.updateLead
              dispatch(setIsEditMode(false));
            },
          }
        );
        // Do not reset() here — it cleared the form to empty defaults before refetch,
        // so view mode showed stale/blank phone & location until a full reload.
      } else {
        createLead.mutate(data, {
          onSuccess: () => {
            toast.success("Lead created successfully");
            dispatch(setIsAddLead(false));
            if (onSuccess) onSuccess();
          },
        });
      }
    },
    [
      isViewLocked,
      isEditMode,
      leadId,
      updateLead,
      createLead,
      dispatch,
      onSuccess,
      requiredFields,
      hiddenFields,
      prefLabelFor,
      spesificFields,
      allowCreatedAtEdit,
      lead,
    ]
  );

  const wrappedHandleSubmit = useCallback<UseFormHandleSubmit<LeadFormData>>(
    (fn) => handleSubmit(fn, onInvalid),
    [handleSubmit]
  );

  const noteOperations = useMemo(
    () => ({
      fields: noteFields,
      append: noteAppend,
      prepend: notePrepend,
      remove: noteRemove,
      update: noteUpdate,
    }),
    [noteFields, noteAppend, notePrepend, noteRemove, noteUpdate]
  );

  const followUpOperations = useMemo(
    () => ({
      fields: followUpFields,
      append: followUpAppend,
      prepend: followUpPrepend,
      remove: followUpRemove,
      update: followUpUpdate,
      replace: followUpReplace,
    }),
    [
      followUpFields,
      followUpAppend,
      followUpPrepend,
      followUpRemove,
      followUpUpdate,
      followUpReplace,
    ]
  );

  const deadlineOperations = useMemo(
    () => ({
      fields: deadlineFields,
      append: deadlineAppend,
      prepend: deadlinePrepend,
      remove: deadlineRemove,
      update: deadlineUpdate,
      replace: deadlineReplace,
    }),
    [
      deadlineFields,
      deadlineAppend,
      deadlinePrepend,
      deadlineRemove,
      deadlineUpdate,
      deadlineReplace,
    ]
  );

  const isSubmitting = createLead.isPending;

  const value = useMemo<FormLeadContextType>(
    () => ({
      register,
      control,
      errors,
      watch,
      getValues,
      setValue,
      handleSubmit: wrappedHandleSubmit,
      reset,
      onSubmit,
      isSubmitting,
      defaultValues: leadDefaultValues,
      lead,
      refetchCurLead,
      noteOperations,
      followUpOperations,
      deadlineOperations,
      isViewLocked,
      revokedBranch: false,
      revokedUser: false,
    }),
    [
      register,
      control,
      errors,
      watch,
      getValues,
      setValue,
      wrappedHandleSubmit,
      reset,
      onSubmit,
      isSubmitting,
      lead,
      refetchCurLead,
      noteOperations,
      followUpOperations,
      deadlineOperations,
      isViewLocked,
    ]
  );

  return (
    <FormLeadContext.Provider value={value}>
      <ChildFormProvider parentProps={{ lead, routeStageId, routeStatusId, routeSubStatusId, defaultChildId }}>
        <IndustryContextWrapper>{children}</IndustryContextWrapper>
      </ChildFormProvider>
    </FormLeadContext.Provider>
  );
}

function useLeadForm(): FormLeadContextType {
  const context = useContext(FormLeadContext);
  if (!context)
    throw new Error("useLeadForm must be used within LeadFormProvider");
  return context;
}

function useLeadNotes() {
  const { noteOperations, lead, getValues } = useLeadForm();
  const { patchLeadSilently } = useLeadMutations();
  const currentLeadId = lead?.id;

  /**
   * Returns `false` when the note was REFUSED (nothing sent) so the composer
   * keeps what was typed; `options.onFailed` fires when a sent note could not
   * be saved, so the composer can take the text back. Either way a comment
   * the user wrote is never simply dropped (2026-09-30).
   */
  const addNote = (
    note: NoteData,
    options?: { onFailed?: () => void },
  ): boolean => {
    // `switchedByName` is a CLIENT-ONLY byline for the optimistic row (see
    // authorByline.ts). The server derives the real author from the session
    // itself, so it never travels in the request.
    const { switchedByName: _byline, ...noteForServer } = note as NoteData & {
      switchedByName?: string | null;
    };

    // NEVER SHOW A NOTE WE CANNOT SAVE (2026-09-29). The prepend used to run
    // first and the PATCH only `if (currentLeadId)` — so on the frames where
    // the lead query hasn't resolved, the note was painted into the thread and
    // no request was ever sent. Nothing failed, nothing was logged, and the
    // note was simply gone at the next refetch.
    if (!currentLeadId) {
      toast.error("This lead is still loading — please add the note again.");
      return false;
    }

    const createdAt = note.createdAt ?? new Date().toISOString();
    noteOperations.prepend({
      ...note,
      createdById: String(note.createdById),
      createdAt,
    });

    patchLeadSilently.mutate(
      {
        id: currentLeadId,
        data: { note: noteForServer },
      },
      {
        // THE OPTIMISTIC ROW MUST NOT OUTLIVE A FAILED SAVE (2026-09-29).
        // The row was prepended and then never taken back, so a PATCH that
        // failed left the note sitting in the thread looking saved — it only
        // disappeared on the next refetch, long after the "Auto-save failed"
        // toast had gone. That is how a note that never reached the database
        // reads as "the note vanished". Matched by timestamp + text and
        // guarded on `!noteId`, so it can only ever remove THIS unsaved row,
        // never a server one (those always carry an id) and never a second
        // note typed in the meantime.
        onError: () => {
          const list = (getValues("notes") as any[]) ?? [];
          const index = list.findIndex(
            (n: any) =>
              !n?.noteId && n?.createdAt === createdAt && n?.note === note.note,
          );
          if (index >= 0) noteOperations.remove(index);
          // …and the words go back to the composer instead of vanishing with
          // the row — the user should never have to retype a comment.
          options?.onFailed?.();
        },
      },
    );
    return true;
  };

  return {
    notes: noteOperations.fields,
    addNote,
    deleteNote: (index: number) => noteOperations.remove(index),
    updateNote: (index: number, note: NoteData) =>
      noteOperations.update(index, note),
  };
}

// ---------------------------------------------------------
// FollowUps Hook (DESC order: newest on top) ✅
// ---------------------------------------------------------
function useLeadFollowUps() {
  const { followUpOperations, getValues, lead, setValue } = useLeadForm();
  const { patchLeadSilently } = useLeadMutations();
  const { currentLeadId: reduxLeadId } = useAppSelector(
    (state) => state.leadFunctional
  );
  const currentLeadId = reduxLeadId ?? lead?.id;

  /**
   * @param options Optional callbacks forwarded to the silent PATCH. The
   * drawer uses them to chain work that must happen only AFTER the row is
   * really saved — creating a follow-up can also apply an unsaved Stage/status
   * pick, and the move must not be attempted on a follow-up that failed to
   * write (see ActionForNextFollowup / PendingStageDraft). Callers that pass
   * nothing behave exactly as before: fire-and-forget.
   */
  const addFollowUp = (
    followUp: any,
    options?: { onSuccess?: () => void; onError?: (error: unknown) => void },
  ) => {
    const currentList = (getValues("followUps") as any[]) ?? [];

    // ROLE-SCOPED local supersede (2026-08-30): only the SAME slot's pending
    // row (or a pre-upgrade role-less one) completes when a new follow-up is
    // added — other roles' pending rows are THEIR follow-ups and stay live,
    // mirroring the server's role-scoped supersede exactly.
    const newRoleId = followUp?.role?.id ?? followUp?.roleId ?? null;
    const completedList = currentList.map((fu) => {
      if (fu?.status === "Complete") return fu;
      const fuRoleId = fu?.role?.id ?? fu?.roleId ?? null;
      if (fuRoleId !== null && newRoleId !== null && fuRoleId !== newRoleId) {
        return fu; // another role's live slot — untouched
      }
      return { ...fu, status: "Complete" };
    });

    const newFollowUp = {
      ...followUp,
      createdAt: followUp.createdAt ?? new Date().toISOString(),
      status: "Pending",
      // ✅ keep required schema keys safe
      isSystem: Boolean(followUp?.isSystem),
      completedBy: followUp?.completedBy ?? {},
    };

    // ✅ ONE state update -> UI always refresh
    followUpOperations.replace([newFollowUp, ...completedList]);
    setValue("followUpDate", newFollowUp.date, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    if (currentLeadId) {
      patchLeadSilently.mutate(
        {
          type: "updateFollowup",
          id: currentLeadId,
          data: { followUp: newFollowUp },
        },
        options
          ? {
              onSuccess: () => options.onSuccess?.(),
              onError: (error) => options.onError?.(error),
            }
          : undefined,
      );
    } else {
      // Nothing was written, so nothing may be chained off it.
      options?.onError?.(new Error("No lead in context"));
    }
  };

  // NOTE: no `completeFollowUp` / `deleteFollowUp`. A follow-up is a single
  // always-live date that can only be RESCHEDULED (2026-08-20) — it is never
  // completed away and never deleted. `addFollowUp` supersedes the previous
  // row server-side, which is the only way the date changes.
  return {
    followUps: followUpOperations.fields,
    addFollowUp,
    updateFollowUp: (index: number, fu: any) =>
      followUpOperations.update(index, fu),
  };
}

function useLeadDeadlines() {
  const { deadlineOperations, getValues, lead } = useLeadForm();
  const { patchLeadSilently } = useLeadMutations();
  const { currentLeadId: reduxLeadId } = useAppSelector(
    (state) => state.leadFunctional
  );
  const currentLeadId = reduxLeadId ?? lead?.id;

  const addDeadline = (deadline: any) => {
    const currentList = (getValues("deadlines") as any[]) ?? [];

    const completedList = currentList.map((d) => {
      if (d?.status === "Complete") return d;
      return { ...d, status: "Complete" };
    });

    const newDeadline = {
      ...deadline,
      createdAt: deadline.createdAt ?? new Date().toISOString(),
      status: "Pending",
      isSystem: Boolean(deadline?.isSystem),
      completedBy: deadline?.completedBy ?? {},
    };

    deadlineOperations.replace([newDeadline, ...completedList]);

    if (currentLeadId) {
      patchLeadSilently.mutate({
        type: "updateDeadline",
        id: currentLeadId,
        data: { deadline: newDeadline },
      });
    }
  };

  const completeDeadline = (
    index: number,
    completedUser: { id: string | number; name: string }
  ) => {
    const current = getValues(`deadlines.${index}`) as any;
    if (!current) return;

    const updated = {
      ...current,
      status: "Complete",
      completedBy: {
        ...(current.completedBy ?? {}),
        id: String(completedUser.id),
        name: completedUser.name,
      },
    };

    deadlineOperations.update(index, updated);

    if (currentLeadId) {
      patchLeadSilently.mutate({
        type: "completeDeadline",
        id: currentLeadId,
        data: { deadline: updated },
      });
    }
  };

  return {
    deadlines: deadlineOperations.fields,
    addDeadline,
    deleteDeadline: (index: number) => deadlineOperations.remove(index),
    updateDeadline: (index: number, d: any) =>
      deadlineOperations.update(index, d),
    completeDeadline,
  };
}

export {
  LeadFormProvider,
  useLeadForm,
  useLeadNotes,
  useLeadFollowUps,
  useLeadDeadlines,
};
